import { RequiredOnboarding } from "./scenes/learning/RequiredOnboarding";
import { useOnboardingStatus } from "./backend/queries/learning";
import React from "react";
import { Navigate, Routes, Route, useParams, useLocation } from "react-router-dom";
import { isAuthStatus, useCurrentUser } from "./backend/queries/auth";
import ProtectedRoute from "./backend/protectedRoute";
import { AppShell } from "./components/shell";
import { ErrorState, LoadingState } from "./components/patterns";
import Invite from "./scenes/login/invite";
import Login from "./scenes/login/login";
import UserManagement from "./scenes/usermangement/userManagement";
import ProjectContentPage from "./scenes/project-content/ProjectContentPage";
import ClientOverview from "./scenes/home/ClientOverview";
import Home from "./scenes/home/home";
import ProjectJourney from "./scenes/project-journey/timeline";
import ResetPassword from "./scenes/global/ResetPassword";
import SetNewPassword from "./scenes/global/setNewPassword";
import ProjectsList from "./scenes/projectlist/projectlist";
import NotFound from "./scenes/error/NotFound";
import NotAuth from "./scenes/error/NotAuth";
import MyProjects from "./scenes/myprojects/myprojects";
import ProfilePage from "./scenes/profile/profile";
import Dashboard from "./scenes/projectdashboard/projectdashboard";
import ProjectDetails from "./scenes/projectlist/projectDetails";
import AgentProfile from "./scenes/agentprofile/agentprofile";
import NotMade from "./scenes/error/NotMade";
import ClientProjectDetails from "./scenes/myprojects/clientProjectdetails";
import ClientBudgetUI from "./scenes/budgeting/ClientBudgetUI";
import TaskListManagement from "./scenes/tasklist/taskListManagement";
import ClientDocument from "./scenes/documents/clientDocument";
import UserDetail from "./scenes/usermangement/userDetail";
import Integrations from "./scenes/integrations/integrations";
import XeroIntegration from "./scenes/integrations/xeroIntegration";
import XeroSettings from "./scenes/integrations/xeroSettings";
import IntegrationDetail from "./scenes/integrations/integrationDetail";
import RunDetail from "./scenes/integrations/runDetail";
import AdminSettings from "./scenes/settings/AdminSettings";
import Messages from "./scenes/chat/messages";
import { ChatSocketProvider } from "./backend/chat/ChatSocketProvider";
import Operations from "./scenes/operations/Operations";
import Learning from "./scenes/learning/Learning";

function RootRedirect() {
  const { data: user, error, isPending, refetch } = useCurrentUser();

  if (isPending) {
    return (
      <div className="portal-page">
        <LoadingState size="page" label="Loading portal" />
      </div>
    );
  }

  if (isAuthStatus(error, 401)) {
    return <Navigate to="/login" replace />;
  }
  if (isAuthStatus(error, 403)) {
    return <Navigate to="/403" replace />;
  }
  if (error) {
    return (
      <div className="portal-page">
        <ErrorState
          size="page"
          title="Portal unavailable"
          description="We could not confirm who you are signed in as."
          onRetry={() => refetch()}
        />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const role = user.role || user.profile?.role;
  const target = role === "Owner" || role === "Admin" || role === "Agent" ? "/dashboard" : "/home";
  return <Navigate to={target} replace />;
}

function LegacyProjectTasksRedirect() {
  const { projectId } = useParams();
  return (
    <Navigate
      to={projectId ? `/projects/${projectId}?view=tasks` : "/projects"}
      replace
    />
  );
}

function App() {
  const { data: user } = useCurrentUser();
  const location = useLocation();
  const needsPassword = user?.profile?.password_setup_required === true;
  const staff = user?.role === "Owner" || user?.role === "Admin" || user?.role === "Agent";
  const onboarding = useOnboardingStatus(staff && !needsPassword ? user?.id : undefined);
  if (needsPassword && location.pathname !== "/set-password") return <Navigate to="/set-password" replace />;
  if (!needsPassword && staff && onboarding.isPending) return <LoadingState size="page" label="Checking onboarding" />;
  if (!needsPassword && staff && onboarding.error) return <ErrorState size="page" title="Could not check onboarding" onRetry={() => onboarding.refetch()} />;
  if (!needsPassword && staff && onboarding.data?.required.length) return <RequiredOnboarding courses={onboarding.data.required} />;

  return (
    <ChatSocketProvider enabled={Boolean(user?.id) && !needsPassword}>
      <div className="app">
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route element={<ProtectedRoute allowedRoles={["Owner", "Admin","Agent"]}><AppShell /></ProtectedRoute>}><Route path="/learning" element={<Learning />} /></Route>

        {/* Unauthenticated and credential-recovery screens carry no shell. */}
        <Route path="/login" element={<Login key="login" />} />
        <Route path="/invite" element={<Invite />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/set-password" element={<SetNewPassword />} />
        <Route path="/403" element={<NotAuth />} />
        <Route path="/under-construction" element={<NotMade />} />

        {/*
          Staff workspace. The shell only presents navigation; Rust authorises
          every route and every read behind it.
        */}
        <Route
          element={
            <ProtectedRoute allowedRoles={["Owner", "Admin", "Agent"]}>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/projects" element={<ProjectsList />} />
          <Route path="/projects/:projectId" element={<ProjectDetails />} />
          <Route
            path="/user-management"
            element={
              <ProtectedRoute allowedRoles={["Owner", "Admin"]}>
                <UserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/:userId"
            element={
              <ProtectedRoute allowedRoles={["Owner", "Admin"]}>
                <UserDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/integrations"
            element={
              <ProtectedRoute allowedRoles={["Owner", "Admin"]}>
                <Integrations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/integrations/xero"
            element={
              <ProtectedRoute allowedRoles={["Owner"]}>
                <XeroIntegration />
              </ProtectedRoute>
            }
          />
          <Route
            path="/integrations/xero/settings"
            element={
              <ProtectedRoute allowedRoles={["Owner"]}>
                <XeroSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/integrations/xero/syncs/:integrationId"
            element={
              <ProtectedRoute allowedRoles={["Owner"]}>
                <IntegrationDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/integrations/xero/syncs/:integrationId/runs/:runId"
            element={
              <ProtectedRoute allowedRoles={["Owner"]}>
                <RunDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute allowedRoles={["Owner", "Admin"]}>
                <AdminSettings />
              </ProtectedRoute>
            }
          />
          <Route path="/myprofile" element={<AgentProfile />} />
          <Route path="/task-list-manager" element={<TaskListManagement />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/operations" element={<Operations />} />

          <Route
            path="/projects/:projectId/client-view"
            element={<ClientProjectDetails mode="agent-peek" />}
          />
          <Route
            path="/agent-tasks"
            element={<Navigate to="/projects" replace />}
          />
          <Route
            path="/tasks/:projectId"
            element={<LegacyProjectTasksRedirect />}
          />
        </Route>

        {/* Client-facing screens, which staff may also open. */}
        <Route
          element={
            <ProtectedRoute allowedRoles={["Owner", "Admin", "Agent", "Client"]}>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route path="/home" element={<ClientOverview />} />
          <Route path="/my-projects/:projectId" element={<Home />} />
          <Route path="/project-journey" element={<ProjectJourney />} />
          <Route path="/my-projects" element={<MyProjects />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/budgeting" element={<ClientBudgetUI />} />
          <Route path="/properties" element={<ProjectContentPage kind="property" />} />
          <Route path="/project-updates" element={<ProjectContentPage kind="update" />} />
          <Route path="/documents" element={<ClientDocument />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
      </div>
    </ChatSocketProvider>
  );
}

export default App;
